import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Input,
  PageHeader,
  Pagination,
  Table,
  type Tone,
} from '../../design';
import { fmtDateTime } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { ExportButton } from '../reports/ExportButton';

type AuditEvent = components['schemas']['AuditEvent'];

const RESULT: Record<AuditEvent['result'], { label: string; tone: Tone }> = {
  success: { label: 'reușit', tone: 'green' },
  denied: { label: 'refuzat', tone: 'orange' },
  failed: { label: 'eșuat', tone: 'red' },
};

export function AuditPage() {
  const { values, page, pageSize, set } = useUrlState([
    'actor',
    'action',
    'from',
    'to',
    'lockerId',
    'orgId',
  ] as const);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const query = useQuery({
    queryKey: ['audit', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/audit', {
          params: {
            query: queryParams({
              actor: values.actor,
              action: values.action,
              from: values.from ? new Date(values.from).toISOString() : undefined,
              to: values.to ? new Date(values.to).toISOString() : undefined,
              lockerId: values.lockerId || undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });
  return (
    <>
      <PageHeader
        title="Jurnal de audit"
        subtitle="Append-only: cine a făcut ce, asupra cărei ținte, cu ce rezultat. Filtrele sunt în adresa paginii."
        actions={
          <ExportButton
            kind="audit"
            params={{
              actor: values.actor || undefined,
              action: values.action || undefined,
              from: values.from ? new Date(values.from).toISOString() : undefined,
              to: values.to ? new Date(values.to).toISOString() : undefined,
              lockerId: values.lockerId || undefined,
              orgId: values.orgId || undefined,
            }}
          />
        }
      />
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Input
            placeholder="Actor (e-mail)…"
            value={values.actor}
            onChange={(e) => set({ actor: e.target.value })}
            style={{ maxWidth: 220 }}
            aria-label="Actor"
          />
          <Input
            placeholder="Acțiune (ex. locker.create)…"
            value={values.action}
            onChange={(e) => set({ action: e.target.value })}
            style={{ maxWidth: 240 }}
            aria-label="Acțiune"
          />
          <Input
            type="datetime-local"
            value={values.from}
            onChange={(e) => set({ from: e.target.value })}
            aria-label="De la"
            style={{ maxWidth: 220 }}
          />
          <Input
            type="datetime-local"
            value={values.to}
            onChange={(e) => set({ to: e.target.value })}
            aria-label="Până la"
            style={{ maxWidth: 220 }}
          />
          {(values.actor || values.action || values.from || values.to) && (
            <Button small onClick={() => set({ actor: null, action: null, from: null, to: null })}>
              Șterge filtrele
            </Button>
          )}
        </div>
        <Table<AuditEvent>
          columns={[
            {
              key: 'at',
              header: 'Când',
              render: (e) => <span className="nowrap">{fmtDateTime(e.at)}</span>,
              width: '160px',
            },
            {
              key: 'actor',
              header: 'Actor',
              render: (e) => (
                <div>
                  <div>{e.actorLabel}</div>
                  <div className="tiny muted">{e.roleKey}</div>
                </div>
              ),
            },
            {
              key: 'action',
              header: 'Acțiune',
              render: (e) => <span className="mono">{e.action}</span>,
            },
            {
              key: 'target',
              header: 'Țintă',
              render: (e) => (
                <span className="small">
                  {e.targetType} <span className="muted mono">{e.targetId.slice(0, 8)}</span>
                </span>
              ),
            },
            {
              key: 'result',
              header: 'Rezultat',
              render: (e) => <Badge tone={RESULT[e.result].tone}>{RESULT[e.result].label}</Badge>,
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(e) => e.id}
          onRowClick={setSelected}
          selectedKey={selected?.id ?? null}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Niciun eveniment.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {selected && (
        <Drawer title={selected.action} onClose={() => setSelected(null)}>
          <div className="stack">
            <div className="flex wrap">
              <Badge tone={RESULT[selected.result].tone}>{RESULT[selected.result].label}</Badge>
              <span className="muted small">{fmtDateTime(selected.at)}</span>
            </div>
            <dl className="small">
              <dt className="muted">Actor</dt>
              <dd>
                {selected.actorLabel} · {selected.roleKey}
              </dd>
              <dt className="muted">Țintă</dt>
              <dd className="mono">
                {selected.targetType} {selected.targetId}
              </dd>
              {selected.lockerId && (
                <>
                  <dt className="muted">Locker</dt>
                  <dd className="mono">{selected.lockerId}</dd>
                </>
              )}
              {selected.orgId && (
                <>
                  <dt className="muted">Organizație</dt>
                  <dd className="mono">{selected.orgId}</dd>
                </>
              )}
            </dl>
            <h3>Detaliu</h3>
            <pre
              className="mono small"
              style={{
                background: 'var(--bg)',
                padding: 12,
                borderRadius: 9,
                overflow: 'auto',
                margin: 0,
              }}
            >
              {JSON.stringify(selected.detail, null, 2)}
            </pre>
          </div>
        </Drawer>
      )}
    </>
  );
}
